import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import { MentionTextarea } from './MentionTextarea';
import EmailDescription from './EmailDescription';
import {
  Drawer,
  Text,
  Loader,
  Button,
  Collapse,
  Skeleton,
  Tooltip,
  Paper,
  Stack,
  Group,
  Alert,
  Textarea,
  Switch,
  NumberInput,
  Select,
  TextInput,
  Modal,
  Divider,
  Checkbox,
  FileButton,
  Popover,
  Indicator,
  ActionIcon,
  Tabs,
  Menu,
} from '@mantine/core';
import {
  IconBuilding,
  IconClock,
  IconExternalLink,
  IconFileText,
  IconHistory,
  IconPaperclip,
  IconSettings,
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
  IconMessage,
  IconActivity,
  IconDownload,
  IconRefresh,
  IconLock,
  IconSparkles,
  IconPencil,
  IconClockPlus,
  IconClockPlay,
  IconSend,
  IconEdit,
  IconTransfer,
  IconUserCheck,
  IconCircleCheck,
  IconUpload,
  IconBell,
  IconX,
  IconDotsVertical,
} from '@tabler/icons-react';
import { HandoverModal } from './HandoverModal';
import { HandoverBanner } from './HandoverBanner';
import { KianAnalysisPanel } from './KianAnalysisPanel';
import { TicketReminderPanel, ticketTodosKey } from './TicketReminderControls';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTicketCockpit } from '../stores/ticketCockpit';
import { useAuthStore } from '../stores/auth';
import { api, TicketCockpitDetail, SlaSeverity, type TicketAssistResponse } from '../lib/api';
import { useRegisterKianContext } from '../hooks/useRegisterKianContext';
import styles from './TicketCockpitDrawer.module.css';

// ============================================
// HELPER FUNCTIONS
// ============================================

function formatAge(hours: number): string {
  if (hours < 1) return '<1h';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Max. Upload-Größe (muss zum Backend-Limit passen, Default 10 MB). */
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/** Liest eine Datei als reines base64 (ohne data:-Präfix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
    reader.readAsDataURL(file);
  });
}

function getPriorityClass(rank?: number, name?: string): string {
  const pName = (name || '').toLowerCase();
  const isHigh = rank === 1 || rank === 2 || rank === 3 ||
    pName.includes('critical') || pName.includes('emergency') ||
    pName.includes('high') || pName === 'p1' || pName === 'p2' || pName === 'p3';
  const isLow = rank === 4 || rank === 5 || pName.includes('low');
  
  if (isHigh) return styles.priorityHigh;
  if (isLow) return styles.priorityLow;
  return styles.priorityMedium;
}

function getSlaClass(severity: SlaSeverity): string {
  switch (severity) {
    case 'CRITICAL':
      return styles.slaCritical;
    case 'SOON':
      return styles.slaSoon;
    case 'OK':
      return styles.slaOk;
    case 'NONE':
      return styles.slaNone;
    default: {
      return '';
    }
  }
}

/** Texte passend zu `calculateSlaSeverity` im Backend (inkl. NONE ohne CW-Termine). */
function formatSlaBadge(sla: TicketCockpitDetail['sla']): string {
  switch (sla.severity) {
    case 'CRITICAL':
      return 'SLA überfällig';
    case 'SOON': {
      const dueSoon = Math.min(
        sla.respondDueInMinutes ?? Infinity,
        sla.resolveDueInMinutes ?? Infinity
      );
      if (!Number.isFinite(dueSoon)) {
        return 'SLA bald fällig';
      }
      const hours = Math.floor(dueSoon / 60);
      const mins = dueSoon % 60;
      return hours > 0 ? `SLA ${hours}h ${mins}m` : `SLA ${mins}m`;
    }
    case 'OK':
      return 'SLA ok';
    case 'NONE':
      return 'Keine SLA-Termine';
    default:
      return `SLA (${String(sla.severity)})`;
  }
}

// ============================================
// SUBCOMPONENTS
// ============================================

interface TimelineListProps {
  ticketId: number;
}

function TimelineList({ ticketId }: TimelineListProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ticket-timeline', ticketId],
    queryFn: () => api.getTicketTimeline(ticketId),
    staleTime: 30000,
  });

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Loader size="sm" color="cyberOrange" />
        <Text size="sm" c="dimmed">Lade Timeline...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <IconAlertTriangle size={24} className={styles.errorIcon} />
        <Text size="sm" c="dimmed">Fehler beim Laden der Timeline</Text>
        <Button size="xs" variant="light" onClick={() => refetch()} leftSection={<IconRefresh size={14} />}>
          Erneut versuchen
        </Button>
      </div>
    );
  }

  const timeline = data?.timeline || [];

  if (timeline.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text size="sm">Keine Einträge vorhanden</Text>
      </div>
    );
  }

  return (
    <>
      {timeline.map((item) => (
        <div key={`${item.kind}-${item.id}`} className={styles.timelineItem}>
          <div className={[
            styles.timelineIcon,
            item.kind === 'NOTE'
              ? (item.isInternal ? styles.timelineIconInternal : styles.timelineIconNote)
              : item.kind === 'TIME_ENTRY'
                ? styles.timelineIconTimeEntry
                : styles.timelineIconActivity
          ].join(' ')}>
            {item.kind === 'NOTE'
              ? (item.isInternal ? <IconLock size={14} /> : <IconMessage size={14} />)
              : item.kind === 'TIME_ENTRY'
                ? <IconClockPlay size={14} />
                : <IconActivity size={14} />
            }
          </div>
          <div className={styles.timelineBody}>
            <div className={styles.timelineMeta}>
              {item.author?.name && (
                <span className={styles.timelineAuthor}>{item.author.name}</span>
              )}
              <span>•</span>
              <span>{formatDate(item.createdAt)}</span>
              {item.isInternal && (
                <span className={styles.internalBadge}>Intern</span>
              )}
              {item.kind === 'TIME_ENTRY' && item.durationHours != null && (
                <span className={styles.internalBadge}>{item.durationHours}h</span>
              )}
            </div>
            {item.title && (
              <div className={styles.timelineTitle}>{item.title}</div>
            )}
            {item.text && (
              <div className={styles.timelineText}>{item.text}</div>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

interface AttachmentsListProps {
  ticketId: number;
}

function AttachmentsList({ ticketId }: AttachmentsListProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ticket-attachments', ticketId],
    queryFn: () => api.getTicketAttachments(ticketId),
    staleTime: 30000,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > ATTACHMENT_MAX_BYTES) {
        throw new Error(`Datei zu groß (max. ${Math.round(ATTACHMENT_MAX_BYTES / (1024 * 1024))} MB)`);
      }
      const base64 = await fileToBase64(file);
      return api.uploadTicketAttachment(ticketId, {
        base64,
        filename: file.name,
        mimeType: file.type || undefined,
      });
    },
    onSuccess: (_res, file) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-attachments', ticketId] });
      notifications.show({ title: 'Anhang hochgeladen', message: file.name, color: 'green' });
    },
    onError: (e: Error) =>
      notifications.show({ title: 'Upload fehlgeschlagen', message: e.message, color: 'red' }),
  });

  const attachments = data?.attachments || [];

  return (
    <Stack gap="sm">
      <FileButton
        onChange={(file) => file && uploadMutation.mutate(file)}
        disabled={uploadMutation.isPending}
      >
        {(props) => (
          <Button
            {...props}
            size="xs"
            variant="light"
            leftSection={<IconUpload size={14} />}
            loading={uploadMutation.isPending}
          >
            Datei hochladen
          </Button>
        )}
      </FileButton>

      {isLoading ? (
        <div className={styles.loadingState}>
          <Loader size="sm" color="cyberOrange" />
          <Text size="sm" c="dimmed">Lade Anhänge...</Text>
        </div>
      ) : error ? (
        <div className={styles.errorState}>
          <IconAlertTriangle size={24} className={styles.errorIcon} />
          <Text size="sm" c="dimmed">Fehler beim Laden der Anhänge</Text>
          <Button size="xs" variant="light" onClick={() => refetch()} leftSection={<IconRefresh size={14} />}>
            Erneut versuchen
          </Button>
        </div>
      ) : attachments.length === 0 ? (
        <div className={styles.emptyState}>
          <Text size="sm">Keine Anhänge vorhanden</Text>
        </div>
      ) : (
        <div className={styles.attachmentsList}>
          {attachments.map((attachment) => (
            <a
              key={attachment.id}
              href={api.getTicketAttachmentDownloadUrl(ticketId, attachment.id)}
              className={styles.attachmentItem}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className={styles.attachmentIcon}>
                <IconPaperclip size={18} />
              </div>
              <div className={styles.attachmentInfo}>
                <div className={styles.attachmentName}>{attachment.name}</div>
                <div className={styles.attachmentMeta}>
                  {formatFileSize(attachment.sizeBytes)}
                  {attachment.createdAt && ` • ${formatDate(attachment.createdAt)}`}
                  {attachment.createdBy && ` • ${attachment.createdBy}`}
                </div>
              </div>
              <IconDownload size={18} className={styles.downloadIcon} />
            </a>
          ))}
        </div>
      )}
    </Stack>
  );
}

// ============================================
// WRITE SUBCOMPONENTS
// ============================================

interface AddNoteFormProps {
  ticketId: number;
  onDone?: () => void;
}

function AddNoteForm({ ticketId, onDone }: AddNoteFormProps) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  // Techniker-Notizen sind standardmäßig intern (nicht für Kunden sichtbar)
  const [isInternal, setIsInternal] = useState(true);
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: () => api.addTicketNote(ticketId, { text: text.trim(), isInternal, mentionedUserIds }),
    onSuccess: () => {
      setText('');
      setIsInternal(true);
      setMentionedUserIds([]);
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      notifications.show({ title: 'Notiz gespeichert', message: '', color: 'green' });
      onDone?.();
    },
    onError: (e: Error) =>
      notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  return (
    <Stack gap="xs" mt="sm">
      <MentionTextarea
        value={text}
        onChange={setText}
        onMentionsChange={setMentionedUserIds}
        placeholder="Notiz eingeben… (@Name zum Erwähnen)"
        minRows={2}
      />
      <Group justify="space-between">
        <Switch
          label="Intern"
          checked={isInternal}
          onChange={(e) => setIsInternal(e.currentTarget.checked)}
          size="sm"
        />
        <Button
          size="xs"
          leftSection={<IconSend size={14} />}
          loading={mutation.isPending}
          disabled={!text.trim()}
          onClick={() => mutation.mutate()}
        >
          Senden
        </Button>
      </Group>
    </Stack>
  );
}

interface AddTimeEntryFormProps {
  ticketId: number;
  onDone?: () => void;
}

function AddTimeEntryForm({ ticketId, onDone }: AddTimeEntryFormProps) {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState<number | string>(60);
  const [notes, setNotes] = useState('');
  const [workType, setWorkType] = useState('Regular');

  const mutation = useMutation({
    mutationFn: () =>
      api.addTicketTimeEntry(ticketId, {
        minutes: typeof minutes === 'string' ? parseInt(minutes, 10) : minutes,
        notes: notes.trim() || undefined,
        workType: workType || undefined,
      }),
    onSuccess: () => {
      setMinutes(60);
      setNotes('');
      // Gebuchte Zeit als TIME_ENTRY in der Timeline sichtbar machen (sonst erst nach manuellem Refresh)
      queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-cockpit', ticketId] });
      notifications.show({ title: 'Zeit gebucht', message: '', color: 'green' });
      onDone?.();
    },
    onError: (e: Error) =>
      notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  const minutesNum = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
  const canSubmit = !isNaN(minutesNum) && minutesNum > 0;

  if (!user?.cwMemberIdentifier) {
    return (
      <Alert color="yellow" variant="light" mt="sm">
        Keine CW Member-ID hinterlegt. Bitte Admin kontaktieren.
      </Alert>
    );
  }

  return (
    <Stack gap="xs" mt="sm">
      <Text size="xs" c="dimmed">Buchung als: {user.displayName}</Text>
      <Group gap="sm">
        <NumberInput
          label="Minuten"
          min={1}
          max={1440}
          step={15}
          value={minutes}
          onChange={setMinutes}
          style={{ flex: 1 }}
          size="sm"
        />
        <TextInput
          label="Arbeitstyp"
          value={workType}
          onChange={(e) => setWorkType(e.currentTarget.value)}
          placeholder="z.B. Regular"
          style={{ flex: 1 }}
          size="sm"
        />
      </Group>
      <Textarea
        label="Notiz (optional)"
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
        minRows={2}
        autosize
        size="sm"
      />
      <Group justify="flex-end">
        <Button
          size="xs"
          leftSection={<IconClockPlus size={14} />}
          loading={mutation.isPending}
          disabled={!canSubmit}
          onClick={() => mutation.mutate()}
        >
          Zeit buchen
        </Button>
      </Group>
    </Stack>
  );
}

// ============================================
// QUICK CLOSE MODAL
// ============================================

interface QuickCloseModalProps {
  opened: boolean;
  onClose: () => void;
  ticket: TicketCockpitDetail;
}

function QuickCloseModal({ opened, onClose, ticket }: QuickCloseModalProps) {
  const queryClient = useQueryClient();
  const boardId = ticket.board?.id;
  const { data: statusData } = useQuery({
    queryKey: ['board-statuses', boardId],
    queryFn: () => api.getBoardStatuses(boardId!),
    enabled: !!boardId && opened,
    staleTime: 2 * 60 * 1000,
  });

  const statusOptions = (statusData?.statuses ?? []).map((s) => ({
    value: String(s.id),
    label: s.name ?? String(s.id),
  }));

  const [statusId, setStatusId] = useState<string | null>(null);
  const [timeAlreadyBooked, setTimeAlreadyBooked] = useState(false);
  const [minutes, setMinutes] = useState<number | string>(30);
  const [workType, setWorkType] = useState('');
  const [timeNotes, setTimeNotes] = useState('');
  const [addComment, setAddComment] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isInternal, setIsInternal] = useState(true);

  // Idempotenz-Guards: verhindern Doppelbuchung, wenn nach erfolgreicher Zeit-/
  // Kommentar-Buchung nur das Status-Update fehlschlägt und der Nutzer erneut klickt.
  const timeBookedRef = useRef(false);
  const commentPostedRef = useRef(false);

  useEffect(() => {
    if (opened) {
      setStatusId(null);
      setTimeAlreadyBooked(false);
      setMinutes(30);
      setWorkType('');
      setTimeNotes('');
      setAddComment(false);
      setCommentText('');
      setIsInternal(true);
      timeBookedRef.current = false;
      commentPostedRef.current = false;
    }
  }, [opened]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['ticket-cockpit', ticket.id] });
    queryClient.invalidateQueries({ queryKey: ['kanban-all-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['my-kanban-tickets-all'] });
    queryClient.invalidateQueries({ queryKey: ['team-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['ticket-timeline', ticket.id] });
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!statusId) throw new Error('Bitte einen Status auswählen');

      if (!timeAlreadyBooked && !timeBookedRef.current) {
        const mins = typeof minutes === 'string' ? parseInt(minutes) : minutes;
        if (!mins || mins <= 0) throw new Error('Bitte gültige Minuten eingeben');
        await api.addTicketTimeEntry(ticket.id, {
          minutes: mins,
          notes: timeNotes || undefined,
          workType: workType || undefined,
        });
        timeBookedRef.current = true;
      }

      if (addComment && commentText.trim() && !commentPostedRef.current) {
        await api.addTicketNote(ticket.id, {
          text: commentText.trim(),
          isInternal,
        });
        commentPostedRef.current = true;
      }

      await api.updateTicket(ticket.id, { statusId: parseInt(statusId) });
    },
    onSuccess: () => {
      invalidate();
      notifications.show({ title: 'Ticket geschlossen', message: '', color: 'green' });
      onClose();
    },
    onError: (e: Error) =>
      notifications.show({ title: 'Fehler beim Schließen', message: e.message, color: 'red' }),
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Ticket #${ticket.id} schließen`}
      size="md"
      centered
    >
      <Stack gap="md">
        <Select
          label="Abschlussstatus"
          placeholder="Status auswählen..."
          data={statusOptions}
          value={statusId}
          onChange={setStatusId}
          required
          searchable
        />

        <Divider label="Zeiterfassung" labelPosition="left" />

        <Checkbox
          label="Zeit wurde bereits gebucht"
          checked={timeAlreadyBooked}
          onChange={(e) => setTimeAlreadyBooked(e.currentTarget.checked)}
        />

        {!timeAlreadyBooked && (
          <Stack gap="xs">
            <Group align="flex-end" gap="sm">
              <NumberInput
                label="Minuten"
                value={minutes}
                onChange={setMinutes}
                min={1}
                max={1440}
                style={{ flex: 1 }}
                size="sm"
              />
              <TextInput
                label="Tätigkeitsart"
                placeholder="Regular"
                value={workType}
                onChange={(e) => setWorkType(e.currentTarget.value)}
                style={{ flex: 1 }}
                size="sm"
              />
            </Group>
            <Textarea
              label="Notiz zur Zeit"
              placeholder="Was wurde gemacht?"
              value={timeNotes}
              onChange={(e) => setTimeNotes(e.currentTarget.value)}
              minRows={2}
              autosize
              size="sm"
            />
          </Stack>
        )}

        <Divider label="Kommentar" labelPosition="left" />

        <Checkbox
          label="Abschlusskommentar hinterlassen"
          checked={addComment}
          onChange={(e) => setAddComment(e.currentTarget.checked)}
        />

        {addComment && (
          <Stack gap="xs">
            <Textarea
              label="Kommentar"
              placeholder="Abschlusshinweis..."
              value={commentText}
              onChange={(e) => setCommentText(e.currentTarget.value)}
              minRows={3}
              autosize
              size="sm"
            />
            <Switch
              label={isInternal ? 'Intern (nicht für Kunden sichtbar)' : 'Öffentlich (für Kunden sichtbar)'}
              checked={isInternal}
              onChange={(e) => setIsInternal(e.currentTarget.checked)}
              color="orange"
              size="sm"
            />
          </Stack>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="subtle" onClick={onClose} disabled={mutation.isPending}>
            Abbrechen
          </Button>
          <Button
            color="red"
            leftSection={<IconCircleCheck size={16} />}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Ticket schließen
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface EditTicketFormProps {
  ticket: TicketCockpitDetail;
}

function EditTicketForm({ ticket }: EditTicketFormProps) {
  const queryClient = useQueryClient();
  const [summary, setSummary] = useState(ticket.summary);
  const [description, setDescription] = useState(ticket.description || '');
  const [statusId, setStatusId] = useState<string | null>(null);
  const [priorityId, setPriorityId] = useState<string | null>(null);

  const boardId = ticket.board?.id;

  const { data: statusData } = useQuery({
    queryKey: ['board-statuses', boardId],
    queryFn: () => api.getBoardStatuses(boardId!),
    enabled: !!boardId,
    staleTime: 2 * 60 * 1000,
  });

  const { data: priorityData } = useQuery({
    queryKey: ['cw-priorities'],
    queryFn: () => api.getPriorities(),
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: () => {
      const updates: { summary?: string; description?: string; statusId?: number; priorityId?: number } = {};
      if (summary.trim() !== ticket.summary) updates.summary = summary.trim();
      if (description.trim() !== (ticket.description || '')) updates.description = description.trim();
      if (statusId) updates.statusId = parseInt(statusId);
      if (priorityId) updates.priorityId = parseInt(priorityId);
      if (Object.keys(updates).length === 0) throw new Error('Keine Änderungen');
      return api.updateTicket(ticket.id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-cockpit', ticket.id] });
      // Board & Ticket-Listen spiegeln Status/Priorität sonst erst nach dem 60s-Refetch
      queryClient.invalidateQueries({ queryKey: ['kanban-all-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['my-kanban-tickets-all'] });
      queryClient.invalidateQueries({ queryKey: ['team-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['pending-claims-on-my-tickets'] });
      notifications.show({ title: 'Ticket aktualisiert', message: '', color: 'green' });
      setStatusId(null);
      setPriorityId(null);
    },
    onError: (e: Error) =>
      e.message === 'Keine Änderungen'
        ? notifications.show({
            title: 'Keine Änderungen',
            message: 'Es wurden keine Felder geändert.',
            color: 'blue',
          })
        : notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  const statusOptions = (statusData?.statuses ?? []).map((s) => ({
    value: String(s.id),
    label: s.name ?? String(s.id),
  }));
  const priorityOptions = (priorityData?.priorities ?? []).map((p) => ({
    value: String(p.id),
    label: p.name ?? String(p.id),
  }));

  return (
    <Stack gap="xs" mt="sm">
      <TextInput
        label="Titel"
        value={summary}
        onChange={(e) => setSummary(e.currentTarget.value)}
        size="sm"
      />
      <Textarea
        label="Beschreibung"
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
        minRows={3}
        autosize
        size="sm"
      />
      <Group gap="sm">
        <Select
          label="Status"
          placeholder={ticket.status.name}
          data={statusOptions}
          value={statusId}
          onChange={setStatusId}
          clearable
          style={{ flex: 1 }}
          size="sm"
        />
        <Select
          label="Priorität"
          placeholder={ticket.priority?.name}
          data={priorityOptions}
          value={priorityId}
          onChange={setPriorityId}
          clearable
          style={{ flex: 1 }}
          size="sm"
        />
      </Group>
      <Group justify="flex-end">
        <Button
          size="xs"
          leftSection={<IconEdit size={14} />}
          loading={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Speichern
        </Button>
      </Group>
    </Stack>
  );
}

// ============================================
// COLLAPSIBLE SECTION (a11y, controlled)
// ============================================

interface SectionProps {
  icon: ReactNode;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  /** Eigener Container ohne Standard-Padding (z. B. Timeline mit eigenem Scroll). */
  flush?: boolean;
}

function Section({ icon, title, open, onToggle, children, flush }: SectionProps) {
  return (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.sectionHeader}
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className={styles.sectionTitle}>
          {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          {icon}
          {title}
        </span>
      </button>
      <Collapse expanded={open}>
        <div className={flush ? styles.timelineContent : styles.sectionContent}>{children}</div>
      </Collapse>
    </div>
  );
}

// ============================================
// FOCUSED ACTION MODALS (Notiz / Zeit)
// ============================================

interface ActionModalProps {
  opened: boolean;
  onClose: () => void;
  ticketId: number;
}

function NoteModal({ opened, onClose, ticketId }: ActionModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title="Notiz hinzufügen" size="md" centered>
      <AddNoteForm ticketId={ticketId} onDone={onClose} />
    </Modal>
  );
}

function TimeModal({ opened, onClose, ticketId }: ActionModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title="Zeit buchen" size="md" centered>
      <AddTimeEntryForm ticketId={ticketId} onDone={onClose} />
    </Modal>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function TicketCockpitDrawer() {
  const { isOpen, ticketId, close } = useTicketCockpit();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 639px)') ?? false;
  const [descriptionOpen, setDescriptionOpen] = useState(true);
  const [reminderPopoverOpen, setReminderPopoverOpen] = useState(false);
  // Siehe WeclappTicketCockpit: das Erinnerungs-Modal hängt per Portal im <body>,
  // ein Klick darin gilt für das Popover sonst als „außerhalb" und schließt beides.
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [assistOpen, setAssistOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(true);
  const [attachmentsOpen, setAttachmentsOpen] = useState(true);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cockpitAssist, setCockpitAssist] = useState<TicketAssistResponse | null>(null);
  const [handoverModalOpen, setHandoverModalOpen] = useState(false);
  const [quickCloseOpen, setQuickCloseOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<string | null>('overview');

  // Reset section states when drawer opens
  useEffect(() => {
    if (isOpen) {
      setDescriptionOpen(true);
      setAssistOpen(false);
      setTimelineOpen(true);
      setAttachmentsOpen(true);
      setMetadataOpen(false);
      setEditOpen(false);
      setCockpitAssist(null);
      setHandoverModalOpen(false);
      setQuickCloseOpen(false);
      setNoteModalOpen(false);
      setTimeModalOpen(false);
      setMobileTab('overview');
    }
  }, [isOpen]);

  useEffect(() => {
    setCockpitAssist(null);
  }, [ticketId]);

  // Fetch ticket detail
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ticket-cockpit', ticketId],
    queryFn: () => api.getTicketCockpitDetail(ticketId!),
    enabled: isOpen && ticketId !== null,
    staleTime: 30000,
  });

  const ticket = data?.ticket;
  const slaBadge = ticket ? formatSlaBadge(ticket.sla) : '';

  const { data: handoverData } = useQuery({
    queryKey: ['ticket-handover-active', ticketId],
    queryFn: () => api.getActiveHandover(ticketId!),
    enabled: isOpen && ticketId !== null,
    staleTime: 15000,
  });
  const activeHandover = handoverData?.handover ?? null;

  const { data: remindersData } = useQuery({
    queryKey: ticketTodosKey('cw', ticketId ?? ''),
    queryFn: () => api.getTicketTodos('cw', ticketId!),
    enabled: isOpen && ticketId !== null,
    staleTime: 30000,
  });
  const activeReminderCount = (remindersData?.todos ?? []).length;

  const claimMutation = useMutation({
    mutationFn: () => api.updateKanbanTicketOwner(ticketId!, user!.cwMemberIdentifier!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-cockpit', ticketId] });
      // Übernahme muss sich sofort in Board & Listen widerspiegeln
      queryClient.invalidateQueries({ queryKey: ['kanban-all-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['my-kanban-tickets-all'] });
      queryClient.invalidateQueries({ queryKey: ['team-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['pending-claims-on-my-tickets'] });
      notifications.show({ title: 'Ticket übernommen', message: '', color: 'teal' });
    },
    onError: (e: Error) =>
      notifications.show({ title: 'Fehler beim Übernehmen', message: e.message, color: 'red' }),
  });

  const kianCwTicket = useMemo(() => {
    if (!isOpen || ticketId == null || !ticket) return null;
    const label = `Ticket #${ticket.id} – ${ticket.summary ?? ''}`.slice(0, 200);
    return {
      type: 'cw-ticket' as const,
      id: String(ticketId),
      label,
      metadata: { company: ticket.company?.name },
    };
  }, [isOpen, ticketId, ticket]);

  useRegisterKianContext(kianCwTicket);

  // Erinnerungs-Control (in Header-Aktionsreihe für Mobil & Desktop wiederverwendet)
  const reminderControl = ticket ? (
    <Popover
      opened={reminderPopoverOpen}
      onChange={setReminderPopoverOpen}
      closeOnClickOutside={!reminderModalOpen}
      position="bottom-end"
      width={340}
      shadow="md"
      withinPortal
      zIndex={600}
    >
      <Popover.Target>
        <Indicator
          label={activeReminderCount > 0 ? String(activeReminderCount) : undefined}
          size={16}
          color="grape"
          disabled={activeReminderCount === 0}
        >
          <Tooltip label="Aufgaben & Erinnerungen">
            <ActionIcon
              variant={activeReminderCount > 0 ? 'light' : 'subtle'}
              color={activeReminderCount > 0 ? 'grape' : 'gray'}
              size="lg"
              onClick={() => setReminderPopoverOpen((o) => !o)}
              aria-label="Erinnerungen"
            >
              <IconBell size={18} />
            </ActionIcon>
          </Tooltip>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <TicketReminderPanel
          onModalOpenChange={setReminderModalOpen}
          cwTicketId={ticket.id}
          ticketSummary={ticket.summary}
          companyName={ticket.company?.name}
        />
      </Popover.Dropdown>
    </Popover>
  ) : null;

  const assistMutation = useMutation({
    mutationFn: () => {
      const t = ticket;
      if (!t) throw new Error('Ticket nicht geladen');
      return api.postTicketAssist({
        summary: t.summary,
        description: t.description || undefined,
        companyName: t.company.name,
        priorities: t.priority?.id
          ? [{ id: t.priority.id, name: t.priority.name || String(t.priority.id) }]
          : [],
        types: t.type?.id
          ? [{ id: t.type.id, name: t.type.name || String(t.type.id) }]
          : [],
      });
    },
    onSuccess: (res) => {
      setCockpitAssist(res);
      setAssistOpen(true);
      notifications.show({
        title: 'Assistenz',
        message: res.source === 'llm' ? 'Vorschläge erhalten — nur zur Orientierung.' : 'Regel-Vorschläge — nur zur Orientierung.',
        color: 'blue',
      });
    },
    onError: (e: Error) =>
      notifications.show({ title: 'Assistenz', message: e.message, color: 'red' }),
  });

  return (
    <Drawer
      opened={isOpen}
      onClose={close}
      position="right"
      size={isMobile ? '100%' : 'lg'}
      withCloseButton={false}
      title={null}
      className={styles.drawer}
      styles={{
        header: { display: 'none' },
        body: { padding: 0, height: '100%' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Custom Header */}
      <div className={styles.drawerHeader}>
        <ActionIcon
          className={styles.closeButton}
          variant="subtle"
          color="gray"
          size="lg"
          onClick={close}
          aria-label="Flyout schließen"
        >
          <IconX size={20} />
        </ActionIcon>

        {isLoading ? (
          <div className={styles.headerContent}>
            <div className={styles.ticketTitle}>
              <Skeleton height={24} width={80} radius="sm" />
              <Skeleton height={24} style={{ flex: 1 }} radius="sm" />
            </div>
            <Skeleton height={18} width={200} radius="sm" />
            <Skeleton height={22} width={300} radius="sm" />
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <IconAlertTriangle size={32} className={styles.errorIcon} />
            <Text c="red.4">Fehler beim Laden des Tickets</Text>
            <Button
              size="sm"
              variant="light"
              onClick={() => refetch()}
              leftSection={<IconRefresh size={16} />}
            >
              Erneut versuchen
            </Button>
          </div>
        ) : ticket ? (
          <div className={styles.headerContent}>
            <div className={styles.ticketTitle}>
              <span className={styles.ticketId}>#{ticket.id}</span>
              <span className={styles.summary}>{ticket.summary}</span>
            </div>

            <div className={styles.companyInfo}>
              <IconBuilding size={14} />
              <span>{ticket.company.name}</span>
              {ticket.site && <span> • {ticket.site.name}</span>}
              {ticket.contact && <span> • {ticket.contact.name}</span>}
            </div>

            {/* Info-Badges (nur Status, keine Aktionen) */}
            <div className={styles.badgesRow}>
              <span className={styles.statusBadge}>{ticket.status.name}</span>
              <span className={[styles.priorityBadge, getPriorityClass(ticket.priority.rank, ticket.priority.name)].join(' ')}>
                {ticket.priority.name}
              </span>
              <span
                className={[styles.slaBadge, getSlaClass(ticket.sla.severity)].join(' ')}
                title="SLA-Status aus ConnectWise (Antwort-/Lösungsziel)"
              >
                {slaBadge}
              </span>
              <span className={styles.ageBadge}>
                <IconClock size={12} />
                {formatAge(ticket.ageHours)}
              </span>
              {ticket.links.cw && (
                <Tooltip label="In ConnectWise öffnen">
                  <a
                    href={ticket.links.cw}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.cwLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconExternalLink size={12} />
                    In CW öffnen
                  </a>
                </Tooltip>
              )}
            </div>

            {/* Aktionsreihe (getrennt von den Info-Badges) */}
            <div className={styles.actionsRow}>
              {isMobile ? (
                <>
                  <Menu position="bottom-start" withinPortal zIndex={600} shadow="md">
                    <Menu.Target>
                      <Button size="sm" variant="light" color="gray" leftSection={<IconDotsVertical size={16} />}>
                        Aktionen
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {!ticket.owner && user?.cwMemberIdentifier && !activeHandover && (
                        <Menu.Item
                          leftSection={<IconUserCheck size={16} />}
                          onClick={() => claimMutation.mutate()}
                        >
                          Übernehmen
                        </Menu.Item>
                      )}
                      {!activeHandover && (
                        <Menu.Item
                          leftSection={<IconTransfer size={16} />}
                          onClick={() => setHandoverModalOpen(true)}
                        >
                          Übergeben
                        </Menu.Item>
                      )}
                      <Menu.Item
                        leftSection={<IconFileText size={16} />}
                        onClick={() => { close(); navigate(`/service-report?ticketId=${ticketId}`); }}
                      >
                        Service Report
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                  {reminderControl}
                </>
              ) : (
                <>
                  {!ticket.owner && user?.cwMemberIdentifier && !activeHandover && (
                    <Tooltip label="Ticket für dich übernehmen">
                      <Button
                        size="xs"
                        variant="light"
                        color="teal"
                        leftSection={<IconUserCheck size={14} />}
                        onClick={() => claimMutation.mutate()}
                        loading={claimMutation.isPending}
                      >
                        Übernehmen
                      </Button>
                    </Tooltip>
                  )}
                  {!activeHandover && (
                    <Tooltip label="Ticket an einen anderen Techniker übergeben">
                      <Button
                        size="xs"
                        variant="light"
                        color="orange"
                        leftSection={<IconTransfer size={14} />}
                        onClick={() => setHandoverModalOpen(true)}
                      >
                        Übergeben
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip label="Neuen Service Report für dieses Ticket starten">
                    <Button
                      size="xs"
                      variant="light"
                      color="blue"
                      leftSection={<IconFileText size={14} />}
                      onClick={() => { close(); navigate(`/service-report?ticketId=${ticketId}`); }}
                    >
                      Service Report
                    </Button>
                  </Tooltip>
                  {reminderControl}
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Body */}
      <div className={styles.drawerBody}>
        {ticket && ((t: TicketCockpitDetail) => {
          const descriptionSection = (
            <Section
              icon={<IconFileText size={16} />}
              title="Beschreibung"
              open={descriptionOpen}
              onToggle={() => setDescriptionOpen((o) => !o)}
            >
              {t.description ? (
                <EmailDescription html={t.description} ticketLayout />
              ) : (
                <div className={styles.noDescription}>Keine Beschreibung vorhanden</div>
              )}
            </Section>
          );

          const kianBlock = ticketId ? (
            <div className={styles.kianBlock}>
              <KianAnalysisPanel ticketId={ticketId} source="CW" ticketEnteredAt={t.enteredAt} />
            </div>
          ) : null;

          const timelineSection = (
            <Section
              icon={<IconHistory size={16} />}
              title="Timeline"
              open={timelineOpen}
              onToggle={() => setTimelineOpen((o) => !o)}
              flush
            >
              {ticketId && <TimelineList ticketId={ticketId} />}
            </Section>
          );

          const attachmentsSection = (
            <Section
              icon={<IconPaperclip size={16} />}
              title="Anhänge"
              open={attachmentsOpen}
              onToggle={() => setAttachmentsOpen((o) => !o)}
            >
              {ticketId && <AttachmentsList ticketId={ticketId} />}
            </Section>
          );

          const editSection = (
            <Section
              icon={<IconPencil size={16} />}
              title="Bearbeiten"
              open={editOpen}
              onToggle={() => setEditOpen((o) => !o)}
            >
              <EditTicketForm ticket={t} />
            </Section>
          );

          const assistSection = (
            <Section
              icon={<IconSparkles size={16} />}
              title="Assistenz (Lesen)"
              open={assistOpen}
              onToggle={() => setAssistOpen((o) => !o)}
            >
              <Stack gap="sm">
                <Text size="xs" c="dimmed">
                  Vorschläge ohne CW-Schreibzugriff. Nicht übernommen, bis du sie in ConnectWise
                  anwendest.
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconSparkles size={14} />}
                  loading={assistMutation.isPending}
                  onClick={() => assistMutation.mutate()}
                >
                  Vorschläge anfordern
                </Button>
                {cockpitAssist?.suggestions?.rationale && (
                  <Paper withBorder p="sm" radius="sm">
                    <Text size="sm">{cockpitAssist.suggestions.rationale}</Text>
                    {cockpitAssist.suggestions.dispatchHint && (
                      <Alert mt="sm" variant="light" color="gray" title="Dispatch">
                        <Text size="xs">{cockpitAssist.suggestions.dispatchHint}</Text>
                      </Alert>
                    )}
                    <Group gap="xs" mt="sm">
                      {cockpitAssist.suggestions.priorityId != null && (
                        <Text size="xs" c="dimmed">
                          Priorität-ID: {cockpitAssist.suggestions.priorityId}
                        </Text>
                      )}
                      {cockpitAssist.suggestions.typeId != null && (
                        <Text size="xs" c="dimmed">
                          Type-ID: {cockpitAssist.suggestions.typeId}
                        </Text>
                      )}
                      {cockpitAssist.suggestions.ownerModeHint && (
                        <Text size="xs" c="dimmed">
                          Zuweisung: {cockpitAssist.suggestions.ownerModeHint}
                        </Text>
                      )}
                    </Group>
                  </Paper>
                )}
              </Stack>
            </Section>
          );

          const metadataSection = (
            <Section
              icon={<IconSettings size={16} />}
              title="Weitere Details"
              open={metadataOpen}
              onToggle={() => setMetadataOpen((o) => !o)}
            >
              <div className={styles.metadataGrid}>
                {t.board && (
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Board</span>
                    <span className={styles.metadataValue}>{t.board.name}</span>
                  </div>
                )}
                {t.type && (
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Type</span>
                    <span className={styles.metadataValue}>{t.type.name}</span>
                  </div>
                )}
                {t.subtype && (
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Subtype</span>
                    <span className={styles.metadataValue}>{t.subtype.name}</span>
                  </div>
                )}
                {t.item && (
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Item</span>
                    <span className={styles.metadataValue}>{t.item.name}</span>
                  </div>
                )}
                {t.team && (
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Team</span>
                    <span className={styles.metadataValue}>{t.team.name}</span>
                  </div>
                )}
                {t.owner && (
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Owner</span>
                    <span className={styles.metadataValue}>{t.owner.name || t.owner.identifier}</span>
                  </div>
                )}
                <div className={styles.metadataItem}>
                  <span className={styles.metadataLabel}>Erstellt</span>
                  <span className={styles.metadataValue}>{formatDateTime(t.enteredAt)}</span>
                </div>
                {t.updatedAt && (
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Aktualisiert</span>
                    <span className={styles.metadataValue}>{formatDateTime(t.updatedAt)}</span>
                  </div>
                )}
                {t.sla.respondBy && (
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>SLA Antwort</span>
                    <span className={styles.metadataValue}>{formatDateTime(t.sla.respondBy)}</span>
                  </div>
                )}
                {t.sla.resolveBy && (
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>SLA Lösung</span>
                    <span className={styles.metadataValue}>{formatDateTime(t.sla.resolveBy)}</span>
                  </div>
                )}
              </div>
            </Section>
          );

          return (
            <div className={styles.content}>
              {activeHandover && (
                <HandoverBanner handover={activeHandover} ticketId={ticketId!} />
              )}

              {isMobile ? (
                <Tabs
                  value={mobileTab}
                  onChange={setMobileTab}
                  keepMounted={false}
                  classNames={{ list: styles.mobileTabsList }}
                >
                  <Tabs.List grow>
                    <Tabs.Tab value="overview" leftSection={<IconFileText size={14} />}>
                      Übersicht
                    </Tabs.Tab>
                    <Tabs.Tab value="history" leftSection={<IconHistory size={14} />}>
                      Verlauf
                    </Tabs.Tab>
                    <Tabs.Tab value="more" leftSection={<IconSettings size={14} />}>
                      Mehr
                    </Tabs.Tab>
                  </Tabs.List>
                  <Tabs.Panel value="overview">
                    {kianBlock}
                    {descriptionSection}
                    {metadataSection}
                  </Tabs.Panel>
                  <Tabs.Panel value="history">
                    {timelineSection}
                    {attachmentsSection}
                  </Tabs.Panel>
                  <Tabs.Panel value="more">
                    {editSection}
                    {assistSection}
                  </Tabs.Panel>
                </Tabs>
              ) : (
                <>
                  {descriptionSection}
                  {kianBlock}
                  {timelineSection}
                  {attachmentsSection}
                  {editSection}
                  {assistSection}
                  {metadataSection}
                </>
              )}
            </div>
          );
        })(ticket)}
      </div>

      {/* Sticky Footer: Kern-Aktionen für Techniker (immer erreichbar) */}
      {ticket && (
        <div className={styles.footer}>
          <Button
            variant="light"
            color="indigo"
            leftSection={<IconMessage size={16} />}
            className={styles.footerBtn}
            onClick={() => setNoteModalOpen(true)}
          >
            Notiz
          </Button>
          <Button
            variant="light"
            color="teal"
            leftSection={<IconClockPlus size={16} />}
            className={styles.footerBtn}
            onClick={() => setTimeModalOpen(true)}
          >
            Zeit
          </Button>
          <Button
            color="red"
            leftSection={<IconCircleCheck size={16} />}
            className={styles.footerBtn}
            onClick={() => setQuickCloseOpen(true)}
          >
            Schließen
          </Button>
        </div>
      )}

      {ticketId && ticket && (
        <HandoverModal
          opened={handoverModalOpen}
          onClose={() => setHandoverModalOpen(false)}
          source="CW"
          cwTicketId={ticketId}
          ticketSummary={ticket.summary}
        />
      )}
      {ticket && (
        <QuickCloseModal
          opened={quickCloseOpen}
          onClose={() => setQuickCloseOpen(false)}
          ticket={ticket}
        />
      )}
      {ticketId !== null && (
        <NoteModal
          opened={noteModalOpen}
          onClose={() => setNoteModalOpen(false)}
          ticketId={ticketId}
        />
      )}
      {ticketId !== null && (
        <TimeModal
          opened={timeModalOpen}
          onClose={() => setTimeModalOpen(false)}
          ticketId={ticketId}
        />
      )}
    </Drawer>
  );
}

